import { useState, useEffect, useRef, useCallback } from 'react';
import { Modal } from 'react-bootstrap';

const CANVAS_SIZE  = 360;
const CROP_RADIUS  = 148;
const CROP_CX      = CANVAS_SIZE / 2;
const CROP_CY      = CANVAS_SIZE / 2;
const CROP_DIAM    = CROP_RADIUS * 2;
const OUTPUT_SIZE  = 200;
const MIN_ZOOM     = 1;
const MAX_ZOOM     = 4;

export default function ImageCropperModal({ show, imageSrc, onCrop, onCancel }) {
    const canvasRef  = useRef(null);
    const imgRef     = useRef(null);
    const imgLoaded  = useRef(false);
    const wrapRef    = useRef(null);

    const [zoom,       setZoom]      = useState(1);
    const [offset,     setOffset]    = useState({ x: 0, y: 0 });
    const [dragging,   setDragging]  = useState(false);
    const dragStart    = useRef(null);

    // Reset state each time a new image is presented
    useEffect(() => {
        if (!show || !imageSrc) return;
        imgLoaded.current = false;
        setZoom(1);
        setOffset({ x: 0, y: 0 });

        const img = new Image();
        img.onload = () => {
            imgLoaded.current = true;
            imgRef.current = img;
            drawCanvas(img, 1, { x: 0, y: 0 });
        };
        img.src = imageSrc;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [show, imageSrc]);

    // Redraw whenever zoom or offset changes
    useEffect(() => {
        if (imgRef.current && imgLoaded.current) {
            drawCanvas(imgRef.current, zoom, offset);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [zoom, offset]);

    function getBaseScale(img) {
        const minSide = Math.min(img.naturalWidth, img.naturalHeight);
        return CROP_DIAM / minSide;
    }

    function clampOffset(img, scale, ox, oy) {
        const renderedW = img.naturalWidth  * scale;
        const renderedH = img.naturalHeight * scale;
        const maxX = Math.max(0, (renderedW - CROP_DIAM) / 2);
        const maxY = Math.max(0, (renderedH - CROP_DIAM) / 2);
        return {
            x: Math.max(-maxX, Math.min(maxX, ox)),
            y: Math.max(-maxY, Math.min(maxY, oy)),
        };
    }

    function drawCanvas(img, z, off) {
        const canvas = canvasRef.current;
        if (!canvas) return;
        const ctx = canvas.getContext('2d');
        const scale = getBaseScale(img) * z;

        const drawX = CROP_CX + off.x - (img.naturalWidth  * scale) / 2;
        const drawY = CROP_CY + off.y - (img.naturalHeight * scale) / 2;

        ctx.clearRect(0, 0, CANVAS_SIZE, CANVAS_SIZE);

        // Fill canvas background (visible in corners)
        ctx.fillStyle = '#f0f0f4';
        ctx.fillRect(0, 0, CANVAS_SIZE, CANVAS_SIZE);

        // Draw the image
        ctx.drawImage(img, drawX, drawY, img.naturalWidth * scale, img.naturalHeight * scale);

        // Frosted overlay ONLY outside the circle (evenodd punch-out — image stays fully visible inside)
        ctx.save();
        ctx.beginPath();
        ctx.rect(0, 0, CANVAS_SIZE, CANVAS_SIZE);
        ctx.arc(CROP_CX, CROP_CY, CROP_RADIUS, 0, Math.PI * 2, true); // true = CCW = hole
        ctx.fillStyle = 'rgba(240,240,244,0.78)';
        ctx.fill('evenodd');
        ctx.restore();

        // Crop circle border
        ctx.save();
        ctx.strokeStyle = 'rgba(111,16,185,0.55)';
        ctx.lineWidth = 2;
        ctx.setLineDash([6, 4]);
        ctx.beginPath();
        ctx.arc(CROP_CX, CROP_CY, CROP_RADIUS, 0, Math.PI * 2);
        ctx.stroke();
        ctx.restore();
    }

    // ── Pointer events ──────────────────────────────────────────────────────

    const onPointerDown = useCallback((e) => {
        e.currentTarget.setPointerCapture(e.pointerId);
        dragStart.current = { px: e.clientX, py: e.clientY, ox: offset.x, oy: offset.y };
        setDragging(true);
    }, [offset]);

    const onPointerMove = useCallback((e) => {
        if (!dragStart.current || !imgRef.current) return;
        const dx = e.clientX - dragStart.current.px;
        const dy = e.clientY - dragStart.current.py;
        const scale = getBaseScale(imgRef.current) * zoom;
        const clamped = clampOffset(imgRef.current, scale,
            dragStart.current.ox + dx,
            dragStart.current.oy + dy);
        setOffset(clamped);
    }, [zoom]);

    const onPointerUp = useCallback(() => {
        dragStart.current = null;
        setDragging(false);
    }, []);

    // Native non-passive wheel listener so e.preventDefault() works. React's
    // synthetic onWheel is registered as passive and would warn otherwise.
    useEffect(() => {
        const el = wrapRef.current;
        if (!el) return;
        const handler = (e) => {
            e.preventDefault();
            setZoom(z => {
                const next = Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, z - e.deltaY * 0.001));
                if (imgRef.current) {
                    const scale = getBaseScale(imgRef.current) * next;
                    setOffset(off => clampOffset(imgRef.current, scale, off.x, off.y));
                }
                return next;
            });
        };
        el.addEventListener('wheel', handler, { passive: false });
        return () => el.removeEventListener('wheel', handler);
    }, [show]);

    const onSliderChange = useCallback((e) => {
        const next = parseFloat(e.target.value);
        setZoom(next);
        if (imgRef.current) {
            const scale = getBaseScale(imgRef.current) * next;
            setOffset(off => clampOffset(imgRef.current, scale, off.x, off.y));
        }
    }, []);

    // ── Apply crop ──────────────────────────────────────────────────────────

    const applyCrop = useCallback(() => {
        const img   = imgRef.current;
        if (!img) return;
        const scale = getBaseScale(img) * zoom;
        const srcR  = CROP_RADIUS / scale;
        const srcX  = img.naturalWidth  / 2 - offset.x / scale - srcR;
        const srcY  = img.naturalHeight / 2 - offset.y / scale - srcR;
        const srcSz = srcR * 2;

        const out = document.createElement('canvas');
        out.width  = OUTPUT_SIZE;
        out.height = OUTPUT_SIZE;
        const ctx  = out.getContext('2d');
        ctx.beginPath();
        ctx.arc(OUTPUT_SIZE / 2, OUTPUT_SIZE / 2, OUTPUT_SIZE / 2, 0, Math.PI * 2);
        ctx.clip();
        ctx.drawImage(img, srcX, srcY, srcSz, srcSz, 0, 0, OUTPUT_SIZE, OUTPUT_SIZE);
        out.toBlob(blob => { if (blob) onCrop(blob); }, 'image/jpeg', 0.92);
    }, [zoom, offset, onCrop]);

    return (
        <Modal
            show={show}
            onHide={onCancel}
            centered
            size="md"
            className="pxl_modalDv pxl-cropper-modal"
            backdrop="static"
        >
            <Modal.Header closeButton>
                <Modal.Title>
                    <div className="pxl-model-header">
                        <h2 className="modal-title">Crop Profile Picture</h2>
                    </div>
                </Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <div className="pxl-cropper-body">
                    <div
                        ref={wrapRef}
                        className={`pxl-cropper-canvas-wrap${dragging ? ' is-dragging' : ''}`}
                        onPointerDown={onPointerDown}
                        onPointerMove={onPointerMove}
                        onPointerUp={onPointerUp}
                        onPointerLeave={onPointerUp}
                    >
                        <canvas
                            ref={canvasRef}
                            width={CANVAS_SIZE}
                            height={CANVAS_SIZE}
                        />
                    </div>

                    <div className="pxl-cropper-zoom-row">
                        <span className="pxl-cropper-zoom-icon">
                            <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
                                <line x1="8" y1="11" x2="14" y2="11"/>
                            </svg>
                        </span>
                        <input
                            type="range"
                            className="pxl-cropper-zoom-slider"
                            min={MIN_ZOOM}
                            max={MAX_ZOOM}
                            step="0.05"
                            value={zoom}
                            onChange={onSliderChange}
                        />
                        <span className="pxl-cropper-zoom-icon">
                            <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
                                <line x1="11" y1="8" x2="11" y2="14"/><line x1="8" y1="11" x2="14" y2="11"/>
                            </svg>
                        </span>
                    </div>

                    <p className="pxl-cropper-hint">Drag to reposition · Scroll or use slider to zoom</p>

                    <div className="pxl-cropper-actions">
                        <button type="button" className="pxl-cropper-cancel-btn" onClick={onCancel}>
                            Cancel
                        </button>
                        <button type="button" className="pxlCommon-btn" onClick={applyCrop}>
                            Apply Crop
                        </button>
                    </div>
                </div>
            </Modal.Body>
        </Modal>
    );
}
